<template>
	<div class="banner-container" :class="{ show }" role="link">
		<div
			class="banner-content pointer"
			@click="bannerClick()"
			:aria-label="`${$t(
				'meetings_banner_meeting_joinbutton'
			)} ${$t('meetings_banner_meeting_started_title', [host.fullname])}`"
		>
			<div class="banner-user-container">
				<div class="host-border">
					<ProfilePizza
						v-if="userIds.length > 0"
						:userIds="userIds"
						includeMe
						size="50px"
					/>
				</div>
				<div class="banner-user-details">
					<div class="join-text">
						{{ $t('meetings_banner_meeting_joinbutton') }}
					</div>
					<div class="started-by" v-show="host.fullname">
						{{ $t('meetings_banner_meeting_started_title', [host.fullname]) }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import store, {
	getChat,
	getCurrentChat,
	getUser,
	getVideosession,
} from '@/store'
import { ProfilePizza } from '@/components/common'

export default {
	name: 'MeetingBanner',
	components: { ProfilePizza },
	props: ['hide'],
	data() {
		const chat = getCurrentChat()
		return {
			store,
			videosession: getVideosession(chat?.videosession_id) || {},
		}
	},

	emits: ['showMeetingBanner'],

	mounted() {
		this.$onEventBus(
			'chat-update|videosession-update',
			() => {
				const chat = getCurrentChat()
				this.videosession = getVideosession(chat?.videosession_id) || {}
			},
			25
		)
	},

	computed: {
		isChatRoute() {
			return this.$route.name === 'chat'
		},
		show() {
			if (this.hide) return false
			const check =
				this.isChatRoute &&
				this.videosession &&
				this.videosession.status === 'ACTIVE' &&
				this.videosession.chat_id === this.store.currentChatId
			this.$emit('showMeetingBanner', check)
			return check
		},
		streamingCount() {
			return this.chat.is_supergroup
				? this.videosession.streamingusers_count - 1
				: 0
		},
		chat() {
			return getChat(this.videosession.bubble_id) || {}
		},
		host() {
			const { host, host_id } = this.videosession
			return host ?? getUser(host_id) ?? {}
		},
		userIds() {
			const { host_id, host, streamingusers = [] } = this.videosession
			const hostId = host_id ?? host?.id ?? null
			const ids = streamingusers.filter((id) => id !== hostId)
			if (hostId) {
				// move host to the start of the list
				ids.unshift(hostId)
			}
			return ids
		},
		one() {
			if (this.chat.is_supergroup)
				return this.videosession.streamingusers_count === 1
			return this.users.length === 1
		},
		two() {
			if (this.chat.is_supergroup)
				return this.videosession.streamingusers_count > 1
			return this.users.length === 2
		},
		three() {
			if (this.chat.is_supergroup) return false
			return this.users.length === 3
		},
		four() {
			if (this.chat.is_supergroup) return false
			return this.users.length >= 4
		},
	},

	methods: {
		hasMore(index) {
			const { streamingusers = [] } = this.videosession
			if (index >= 3 && streamingusers.length > 4) {
				// standard groups show count only if more than 4
				return `+${streamingusers.length - 3}`
			}
			return null
		},

		bannerClick() {
			EventBus.$emit(
				'openPopout',
				window.location.origin + `/meeting/${this.chat.id}`
			)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$banner-height: 70px;
$blue: #5a91f9;
$lg-avatar: 48px;
$sm-avatar: 30px;
$lg-border-width: 8px;
$border-width: 3px;
$overlap: -10px;
$overhang: 15px;

@keyframes border-color {
	0%,
	100% {
		background: #37dac0;
	}
	50% {
		background: #20acff;
	}
}

@media screen and (max-width: 800px) {
	.banner-container {
		width: 100vw !important;
	}
	.banner-content {
		width: 100vw !important;
	}
}

.banner-container {
	grid-area: banner;
	width: 100%;
	height: 0;
	transition: height ease-out 150ms;
	margin-bottom: (-$overhang);
	overflow: hidden;
	z-index: 1;
	display: none;
	&.show {
		display: block;
		height: $banner-height + $overhang;
	}
}
.banner-content {
	width: 100%;
	background: $blue;
	color: white;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 16px;
	height: $banner-height;
	box-shadow: 0 5px 8px 0 rgba(0, 0, 0, 0.25);
}
.banner-user-container {
	display: flex;
	align-items: center;
	flex: 1;
	.banner-user-details {
		flex: 1;
	}
}

.host-border {
	margin-right: 12px;
	padding-top: 2px;
	padding-left: 2px;
	width: 54px;
	height: 54px;
	border-radius: 50%;
	background-size: 400% 400%;
	animation: border-color 2s ease infinite;

	> .profile-pizza,
	> .profile-pic-avatar {
		border-color: transparent;
	}

	:deep(.profile-pizza) {
		.profile-more-count {
			background-color: #0d43aa; // magic number from testing various sizes
		}
	}
}
.banner-users {
	display: flex;
}
.join-link {
	cursor: pointer;
	text-decoration: none;
}

.join-text {
	color: #0d43aa;
	font-size: 16px;
	font-family: Avenir-Heavy;
}

.started-by {
	font-size: 14px;
	font-family: Avenir-Heavy;
}
</style>
